import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Avatar,
  Alert,
  Divider,
  IconButton,
  Fade,
  Grow
} from '@mui/material';
import {
  Business,
  VerifiedUser,
  Dashboard,
  LocalShipping,
  Analytics,
  Inventory,
  Payment,
  Support,
  Star,
  ArrowForward,
  CheckCircle,
  Schedule,
  ManageAccounts,
  Speed,
  Security,
  TrendingUp,
  PhoneAndroid
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface StepData {
  label: string;
  description: string;
  details: string[];
  icon: React.ReactNode;
  color: string;
}

interface FeatureData {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

const HowItWorksPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [showFeatures, setShowFeatures] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const timer = setTimeout(() => setShowFeatures(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const steps: StepData[] = [
    {
      label: 'Business Registration',
      description: 'Register your business with comprehensive details and documentation',
      details: [
        'Complete business information form with all required details',
        'Upload required documents (licenses, permits, certificates)',
        'Provide contact information and operational details',
        'Set up your initial business profile and branding'
      ],
      icon: <Business sx={{ fontSize: 32 }} />,
      color: '#2196F3'
    },
    {
      label: 'Verification Process',
      description: 'Our expert team carefully verifies your business credentials',
      details: [
        'Comprehensive document review and authentication',
        'Business legitimacy and ownership verification',
        'Compliance check with local regulations and standards',
        'Quality standards assessment and approval process'
      ],
      icon: <VerifiedUser sx={{ fontSize: 32 }} />,
      color: '#4CAF50'
    },
    {
      label: 'Platform Access & Setup',
      description: 'Gain secure access to your personalized vendor dashboard',
      details: [
        'Receive secure login credentials and account activation',
        'Complete guided platform orientation and training',
        'Customize your dashboard according to your preferences',
        'Access comprehensive setup guidance and support'
      ],
      icon: <Dashboard sx={{ fontSize: 32 }} />,
      color: '#2196F3'
    },
    {
      label: 'Business Management',
      description: 'Manage all aspects of your delivery business efficiently',
      details: [
        'Real-time order management and processing system',
        'Advanced inventory tracking and automated updates',
        'Comprehensive analytics and performance insights',
        'Integrated customer communication and support tools'
      ],
      icon: <ManageAccounts sx={{ fontSize: 32 }} />,
      color: '#4CAF50'
    }
  ];

  const features: FeatureData[] = [
    {
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      title: 'Smart Order Management',
      description: 'Streamlined order processing with real-time tracking, automated notifications, and intelligent routing optimization.',
      color: '#2196F3'
    },
    {
      icon: <Analytics sx={{ fontSize: 40 }} />,
      title: 'Advanced Analytics',
      description: 'Comprehensive business insights with detailed reports on sales, performance metrics, and customer behavior patterns.',
      color: '#4CAF50'
    },
    {
      icon: <Inventory sx={{ fontSize: 40 }} />,
      title: 'Inventory Control',
      description: 'Efficient stock management with real-time tracking, low inventory alerts, and automated reordering capabilities.',
      color: '#FF9800'
    },
    {
      icon: <Payment sx={{ fontSize: 40 }} />,
      title: 'Secure Payments',
      description: 'Robust payment gateway supporting multiple payment methods with instant settlements and fraud protection.',
      color: '#9C27B0'
    },
    {
      icon: <Support sx={{ fontSize: 40 }} />,
      title: '24/7 Support',
      description: 'Round-the-clock customer support with dedicated account management and priority assistance channels.',
      color: '#F44336'
    },
    {
      icon: <Star sx={{ fontSize: 40 }} />,
      title: 'Rating & Reviews',
      description: 'Comprehensive feedback system enabling customer ratings and reviews to build trust and improve service quality.',
      color: '#FF5722'
    }
  ];

  const benefits = [
    { icon: <Speed />, title: 'Fast Setup', description: 'Get started in minutes, not hours' },
    { icon: <Security />, title: 'Secure Platform', description: 'Enterprise-grade security protection' },
    { icon: <TrendingUp />, title: 'Grow Revenue', description: 'Increase sales with our tools' },
    { icon: <PhoneAndroid />, title: 'Mobile Ready', description: 'Manage on-the-go with mobile app' }
  ];

  const handleGetStarted = () => {
    navigate('/vendor/register');
  };

  const handleLearnMore = () => {
    navigate('/vendor/features');
  };

  const handleNextStep = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Fade in timeout={800}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom fontWeight="bold" color="primary">
            What You Need, When You Need It.
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
              Join thousands of successful vendors who have transformed their delivery business with our comprehensive platform. 
              Follow these simple steps to get started and unlock your business potential.
            </Typography>
            <Chip 
              label="Trusted by 1000+ Vendors" 
              color="primary" 
              size="medium"
              sx={{ fontSize: '1rem', py: 3, px: 2 }}
            />
          </Box>
        </Fade>
      </Box>

      {/* Quick Benefits */}
      <Paper elevation={2} sx={{ p: 3, mb: 6, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center" sx={{ mb: 3 }}>
          Why Choose MWM?
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 3,
          justifyContent: 'center'
        }}>
          {benefits.map((benefit, index) => (
            <Grow in timeout={600 + (index * 200)} key={index}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                minWidth: 200,
                flex: '1 1 200px'
              }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {benefit.icon}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {benefit.description}
                  </Typography>
                </Box>
              </Box>
            </Grow>
          ))}
        </Box>
      </Paper>

      {/* Process Steps */}
      <Paper elevation={3} sx={{ p: 4, mb: 6, borderRadius: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" textAlign="center" sx={{ mb: 4 }}>
          Your Journey to Success
        </Typography>
        
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2">
            <strong>Average Setup Time:</strong> 24-48 hours from registration to going live. 
            Our verification process ensures quality and builds customer trust.
          </Typography>
        </Alert>

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                StepIconComponent={() => (
                  <Avatar 
                    sx={{ 
                      bgcolor: step.color,
                      width: 56,
                      height: 56,
                      border: activeStep === index ? '3px solid' : 'none',
                      borderColor: 'primary.main'
                    }}
                  >
                    {activeStep > index ? <CheckCircle /> : 
                     activeStep === index ? <Schedule /> : step.icon}
                  </Avatar>
                )}
              >
                <Typography variant="h5" fontWeight="bold" sx={{ ml: 2 }}>
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ ml: 9, pb: 3 }}>
                  <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                    {step.description}
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    {step.details.map((detail, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                        <CheckCircle sx={{ fontSize: 20, color: 'success.main', mr: 1.5, mt: 0.25 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {detail}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleNextStep}
                      disabled={index === steps.length - 1}
                      endIcon={<ArrowForward />}
                      sx={{ borderRadius: 2 }}
                    >
                      Next Step
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handlePrevStep}
                      disabled={index === 0}
                      sx={{ borderRadius: 2 }}
                    >
                      Previous
                    </Button>
                  </Box>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Platform Features */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" textAlign="center" sx={{ mb: 2 }}>
          Powerful Features for Your Success
        </Typography>
        <Typography variant="body1" textAlign="center" color="textSecondary" sx={{ mb: 4, maxWidth: 700, mx: 'auto' }}>
          Our comprehensive platform provides everything you need to manage, grow, and optimize your delivery business.
        </Typography>

        <Box sx={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          justifyContent: 'center'
        }}>
          {features.map((feature, index) => (
            <Grow in={showFeatures} timeout={400 + (index * 150)} key={index}>
              <Card 
                sx={{ 
                  flex: '1 1 300px',
                  maxWidth: 380,
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                  },
                  borderRadius: 3
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: feature.color,
                      width: 70,
                      height: 70,
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ flex: 1, lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          ))}
        </Box>
      </Box>

      
    </Container>
  );
};

export default HowItWorksPage;